/* global globalThis */
import { recordingError } from './recordingErrors'

export function pickRecorderFormat (MediaRecorder = globalThis.MediaRecorder) {
  if (!MediaRecorder?.isTypeSupported) return null
  if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) return { mimeType: 'audio/webm;codecs=opus', format: 'webm' }
  if (MediaRecorder.isTypeSupported('audio/mp4')) return { mimeType: 'audio/mp4', format: 'mp4' }
  return null
}

// Each attempt gets fresh credentials. A disconnected byte stream cannot resume.
export class ErsStreamSession {
  constructor ({ getToken, referenceText = '', lang = 'en-US', onStream = () => {}, onState = () => {}, onTranscript = () => {}, onComplete = () => {}, onError = () => {}, onDuration = () => {}, platform = globalThis }) {
    Object.assign(this, { getToken, referenceText, lang, onStream, onState, onTranscript, onComplete, onError, onDuration, platform })
    this.state = 'idle'
    this.ended = false
    this.confirmedText = []
    this.sentBytes = 0
  }

  setState (state) {
    this.state = state
    this.onState(state)
  }

  async start () {
    if (this.state !== 'idle' || this.ended) return
    this.setState('preparing')
    try {
      if (this.lang.toLowerCase() === 'zh-cn') throw recordingError('UNSUPPORTED_LANGUAGE')
      if (typeof this.referenceText !== 'string') throw recordingError('BAD_MESSAGE')
      this.picked = pickRecorderFormat(this.platform.MediaRecorder)
      if (!this.picked || !this.platform.WebSocket || !this.platform.navigator?.mediaDevices?.getUserMedia) {
        throw recordingError('UNSUPPORTED_BROWSER')
      }
      // Ask permission first so a long permission prompt cannot expire the WS token.
      const stream = await this.platform.navigator.mediaDevices.getUserMedia({ audio: true })
      if (this.ended) {
        stream.getTracks().forEach(track => track.stop())
        return
      }
      this.stream = stream
      stream.getTracks().forEach(track => {
        track.onended = () => {
          if (!this.ended && this.state !== 'finishing') this.fail(recordingError('MICROPHONE_ENDED'))
        }
      })
      this.onStream(stream)
      if (this.ended) return
      this.setState('connecting')
      this.abortController = new AbortController()
      this.setDeadline(15000)
      const credentials = await this.getToken({ lang: this.lang, signal: this.abortController.signal })
      if (this.ended) return
      if (!credentials?.token || !credentials.wsUrl) throw recordingError('TOKEN_UNAVAILABLE')
      const url = new URL(credentials.wsUrl, this.platform.location.href)
      if (!['ws:', 'wss:'].includes(url.protocol) || (this.platform.location.protocol === 'https:' && url.protocol !== 'wss:')) {
        throw recordingError('TOKEN_UNAVAILABLE')
      }
      url.searchParams.set('token', credentials.token)
      this.sessionId = credentials.sessionId
      this.ws = new this.platform.WebSocket(url.href)
      this.ws.onopen = () => {
        if (this.ended) return
        this.send({ type: 'start', text: this.referenceText, lang: this.lang, format: this.picked.format })
      }
      this.ws.onmessage = event => this.onMessage(event)
      this.ws.onerror = () => this.fail(recordingError('CONNECTION_CLOSED'))
      this.ws.onclose = () => {
        if (!this.ended) this.fail(recordingError('CONNECTION_CLOSED'))
      }
    } catch (error) {
      if (!this.ended) this.fail(error)
    }
  }

  send (message) {
    if (this.ended) return false
    if (this.ws?.readyState !== 1) {
      this.fail(recordingError('CONNECTION_CLOSED'))
      return false
    }
    try {
      this.ws.send(JSON.stringify(message))
      return true
    } catch (error) {
      this.fail(error)
      return false
    }
  }

  startRecorder () {
    if (this.state !== 'connecting') return
    clearTimeout(this.deadline)
    this.recorder = new this.platform.MediaRecorder(this.stream, { mimeType: this.picked.mimeType })
    this.recorder.ondataavailable = ({ data }) => {
      if (this.ended || this.serverFinishing || !data.size) return
      if (this.ws?.readyState !== 1) return this.fail(recordingError('CONNECTION_CLOSED'))
      // Do not drop any chunks: ffmpeg needs the continuous container byte stream.
      if (this.ws.bufferedAmount + data.size > 4 * 1024 * 1024) return this.fail(recordingError('NETWORK_SLOW'))
      try {
        this.ws.send(data)
        this.sentBytes += data.size
      } catch (error) {
        this.fail(error)
      }
    }
    this.recorder.onerror = () => this.fail(recordingError('MICROPHONE_ENDED'))
    this.recorder.onstop = () => {
      if (this.ended) return
      this.releaseMicrophone()
      if (this.state !== 'finishing') return this.fail(recordingError('MICROPHONE_ENDED'))
      if (!this.serverFinishing) {
        if (!this.sentBytes) return this.fail(recordingError('EMPTY_AUDIO'))
        // MediaRecorder emits its final dataavailable before this stop event.
        this.send({ type: 'stop' })
      }
    }
    this.recorder.start(250)
    this.setState('recording')
    const startedAt = Date.now()
    this.durationTimer = setInterval(() => {
      const seconds = Math.floor((Date.now() - startedAt) / 1000)
      this.onDuration(seconds)
      if (seconds >= 600) this.stop()
    }, 250)
  }

  onMessage (event) {
    if (this.ended) return
    let msg
    try {
      msg = JSON.parse(event.data)
      if (!msg || typeof msg.type !== 'string') throw recordingError('BAD_MESSAGE')
    } catch {
      return this.fail(recordingError('BAD_MESSAGE'))
    }
    try {
      switch (msg.type) {
        case 'ready':
          this.startRecorder()
          break
        case 'recognizing':
          this.onTranscript({ confirmed: this.confirmedText.join(this.lang.startsWith('ja') ? '' : ' '), interim: msg.text || '' })
          break
        case 'recognized':
          if (msg.text) this.confirmedText.push(msg.text)
          this.onTranscript({ confirmed: this.confirmedText.join(this.lang.startsWith('ja') ? '' : ' '), interim: '' })
          break
        case 'timeout':
          this.stop(true)
          break
        case 'complete':
          this.ended = true
          this.cleanup()
          this.setState('complete')
          this.onComplete(msg)
          break
        case 'error':
          this.fail(recordingError(msg.code || 'AZURE_ERROR'))
          break
      }
    } catch (error) {
      this.fail(error)
    }
  }

  stop (serverFinishing = false) {
    if (this.ended) return
    if (this.state === 'finishing' && !serverFinishing) return
    if (!['recording', 'finishing'].includes(this.state)) {
      if (!serverFinishing) return this.cancel()
    }
    this.serverFinishing = serverFinishing
    this.setState('finishing')
    clearInterval(this.durationTimer)
    this.setDeadline(serverFinishing ? 10000 : 30000)
    try {
      if (this.recorder?.state !== 'inactive' && this.recorder) this.recorder.stop()
      this.releaseMicrophone()
    } catch (error) {
      this.fail(error)
    }
  }

  setDeadline (ms) {
    clearTimeout(this.deadline)
    this.deadline = setTimeout(() => this.fail(recordingError('TIMEOUT')), ms)
  }

  releaseMicrophone () {
    if (this.stream) {
      this.stream.getTracks().forEach(track => {
        track.onended = null
        track.stop()
      })
    }
    this.stream = null
  }

  cleanup () {
    clearTimeout(this.deadline)
    clearInterval(this.durationTimer)
    if (this.abortController) this.abortController.abort()
    if (this.recorder) {
      this.recorder.ondataavailable = this.recorder.onstop = this.recorder.onerror = null
      if (this.recorder.state !== 'inactive') this.recorder.stop()
    }
    this.releaseMicrophone()
    if (this.ws) {
      this.ws.onopen = this.ws.onmessage = this.ws.onclose = this.ws.onerror = null
      if (this.ws.readyState < 2) this.ws.close()
    }
    this.recorder = this.ws = null
  }

  fail (error) {
    if (this.ended) return
    this.ended = true
    this.cleanup()
    this.setState('error')
    this.onError(error)
  }

  cancel () {
    if (this.ended) return
    this.ended = true
    this.cleanup()
    this.setState('idle')
  }
}
